
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { NotificationIcon } from "@/components/NotificationIcon";
import { useNotifications } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { 
  CheckIcon, 
  BellIcon,
  HeartIcon,
  MessageSquareIcon,
  UserPlusIcon,
  BookmarkIcon
} from "lucide-react";
import { format } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";

export function NotificationsPopover() {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'like':
        return <HeartIcon className="h-4 w-4 text-red-500" />;
      case 'comment':
        return <MessageSquareIcon className="h-4 w-4 text-blue-500" />;
      case 'follow':
        return <UserPlusIcon className="h-4 w-4 text-green-500" />;
      case 'save':
        return <BookmarkIcon className="h-4 w-4 text-purple-500" />;
      default:
        return <BellIcon className="h-4 w-4 text-primary" />;
    }
  };

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    
    // Navigate based on notification type
    if (notification.relatedId && notification.type) {
      switch (notification.type) {
        case 'like':
        case 'comment':
        case 'save':
          navigate(`/prompt/${notification.relatedId}`);
          break;
        case 'follow':
          navigate(`/profile/${notification.relatedId}`);
          break;
        default:
          // Do nothing for unknown types
          break;
      }
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <NotificationIcon count={unreadCount} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="text-sm font-medium">Notifications</h4>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className="text-xs h-auto py-1"
            >
              <CheckIcon className="h-3 w-3 mr-1" />
              Mark all as read
            </Button>
          )}
        </div>
        
        {loading ? (
          <div className="p-4 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : notifications.length > 0 ? (
          <ScrollArea className="h-80">
            {notifications.map((notification) => (
              <div key={notification.id}>
                <button
                  className={`w-full text-left p-4 flex items-start gap-3 hover:bg-muted transition-colors ${
                    !notification.isRead ? 'bg-muted/50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm">{notification.content}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(notification.createdAt), 'PPpp')}
                    </p>
                  </div>
                  {!notification.isRead && (
                    <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                  )}
                </button>
                <Separator />
              </div>
            ))}
          </ScrollArea>
        ) : (
          <div className="p-4 text-center text-muted-foreground">
            <p>No notifications yet</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
